import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AcceptAdminInviteAction,
  ValidateAdminInviteAction,
} from "../../store/action";
import AuthShell from "../ui/AuthShell";
import {
  fieldClass,
  fieldErrorClass,
  labelClass,
  primaryButtonClass,
  textLinkClass,
} from "../ui/formStyles";

/** Mirrors the API's minimum (shared with signup/reset) so the form rejects
 * what the API would reject. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Landing page for the emailed administrator-invite link
 * (/accept-admin-invite?token=...).
 *
 * Same checking → act → done/invalid shape as AcceptInvite.jsx, and separate
 * from it on purpose: that page redeems an invitation into somebody's
 * organization and has to reason about which one, while this creates platform
 * staff, who belong to none. Sharing the flow would mean one component holding
 * both meanings of "invite".
 *
 * The email is never a field here. It comes from the invitation, so a link that
 * gets forwarded cannot mint a staff account under a different address.
 */
const AcceptAdminInvite = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [phase, setPhase] = useState("checking");
  const [invite, setInvite] = useState(null);

  useEffect(() => {
    if (!token) {
      setPhase("invalid");
      return;
    }

    let cancelled = false;

    const check = async () => {
      try {
        const data = await ValidateAdminInviteAction(token);
        if (cancelled) return;

        if (!data?.valid) {
          setPhase("invalid");
          return;
        }

        setInvite(data);
        setPhase("form");
      } catch (err) {
        if (cancelled) return;
        setPhase("invalid");
      }
    };

    check();

    // Validation is read-only, so this is safe to run twice — the flag only
    // stops a late response from overwriting newer state.
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <AuthShell className="accept-admin-invite-content w-full max-w-md">
      <div className="text-center">
        <span
          className={`inline-flex size-12 items-center justify-center rounded-xl ${
            phase === "invalid"
              ? "bg-red-50 text-red-600"
              : "bg-brand-50 text-brand-600"
          }`}
        >
          <i
            className={`bx ${phase === "done" ? "bx-check" : "bx-shield-quarter"} text-2xl`}
            aria-hidden="true"
          />
        </span>

        <h1 className="accept-admin-invite-title mt-4 text-fluid-xl font-bold tracking-tight text-ink-900">
          {phase === "checking" && t("AcceptAdminInvite.checking")}
          {phase === "form" && t("AcceptAdminInvite.formTitle")}
          {phase === "done" && t("AcceptAdminInvite.doneTitle")}
          {phase === "invalid" && t("AcceptAdminInvite.invalidTitle")}
        </h1>

        {phase === "form" && invite && (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {t("AcceptAdminInvite.subtitle", {
              invitedByName: invite.invitedByName,
            })}
          </p>
        )}

        {phase === "invalid" && (
          <p className="mt-2 text-fluid-sm text-ink-500" role="alert">
            {t("AcceptAdminInvite.invalidBody")}
          </p>
        )}

        {phase === "done" && (
          <p className="mt-2 text-fluid-sm text-ink-500">
            {t("AcceptAdminInvite.doneBody")}
          </p>
        )}
      </div>

      {phase === "form" && (
        <AcceptForm
          token={token}
          email={invite?.email}
          onDone={() => setPhase("done")}
        />
      )}

      {(phase === "done" || phase === "invalid") && (
        <Link to="/login" className={`${primaryButtonClass} mt-7`}>
          {phase === "done"
            ? t("AcceptAdminInvite.continueToLogin")
            : t("AcceptAdminInvite.backToLogin")}
        </Link>
      )}

      {phase === "form" && (
        <span className="mt-6 block text-center text-fluid-sm text-ink-500">
          {t("AcceptAdminInvite.backToLoginPrefix")}{" "}
          <Link to="/login" className={textLinkClass}>
            {t("AcceptAdminInvite.backToLoginLink")}
          </Link>
        </span>
      )}
    </AuthShell>
  );
};

/** Name and password. The address is the invitation's, and shown read-only. */
const AcceptForm = ({ token, email, onDone }) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setValidationError(
        t("AcceptAdminInvite.form.passwordTooShort", {
          count: MIN_PASSWORD_LENGTH,
        }),
      );
      return;
    }

    if (password !== confirmPassword) {
      setValidationError(t("AcceptAdminInvite.form.passwordMismatch"));
      return;
    }

    setValidationError("");
    setSubmitting(true);

    try {
      await AcceptAdminInviteAction({ token, firstName, lastName, password });
      onDone();
    } catch (error) {
      setValidationError(
        error.response?.data?.message || t("AcceptAdminInvite.genericError"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      {email && (
        <p className="rounded-lg bg-ink-50 px-4 py-3 text-center text-fluid-sm text-ink-700">
          {t("AcceptAdminInvite.form.emailNotice", { email })}
        </p>
      )}

      <div className="form-row mt-4 grid gap-4 sm:grid-cols-2">
        <div className="form-item">
          <label htmlFor="accept-admin-first-name" className={labelClass}>
            {t("AcceptAdminInvite.form.firstName")}
          </label>
          <input
            type="text"
            id="accept-admin-first-name"
            name="firstName"
            className={fieldClass}
            placeholder={t("AcceptAdminInvite.form.firstNamePlaceholder")}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            autoComplete="given-name"
            required
          />
        </div>
        <div className="form-item">
          <label htmlFor="accept-admin-last-name" className={labelClass}>
            {t("AcceptAdminInvite.form.lastName")}
          </label>
          <input
            type="text"
            id="accept-admin-last-name"
            name="lastName"
            className={fieldClass}
            placeholder={t("AcceptAdminInvite.form.lastNamePlaceholder")}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            autoComplete="family-name"
            required
          />
        </div>
      </div>

      <div className="form-item mt-4">
        <label htmlFor="accept-admin-password" className={labelClass}>
          {t("AcceptAdminInvite.form.password")}
        </label>
        <input
          type="password"
          id="accept-admin-password"
          name="password"
          className={fieldClass}
          placeholder={t("AcceptAdminInvite.form.password")}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          required
        />
        <p className="mt-1.5 text-xs text-ink-400">
          {t("AcceptAdminInvite.form.passwordHint", {
            count: MIN_PASSWORD_LENGTH,
          })}
        </p>
      </div>

      <div className="form-item mt-4">
        <label htmlFor="accept-admin-confirm-password" className={labelClass}>
          {t("AcceptAdminInvite.form.confirmPassword")}
        </label>
        <input
          type="password"
          id="accept-admin-confirm-password"
          name="confirmPassword"
          className={validationError ? fieldErrorClass : fieldClass}
          placeholder={t("AcceptAdminInvite.form.confirmPassword")}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          autoComplete="new-password"
          aria-invalid={validationError ? "true" : undefined}
          aria-describedby={
            validationError ? "accept-admin-password-error" : undefined
          }
          required
        />
        {validationError && (
          <div
            id="accept-admin-password-error"
            role="alert"
            className="error-message mt-1.5 flex items-center gap-1.5 text-xs font-medium text-red-600"
          >
            <i className="bi bi-exclamation-circle-fill" aria-hidden="true" />
            {validationError}
          </div>
        )}
      </div>

      <div className="form-item mt-7">
        <button
          type="submit"
          className={primaryButtonClass}
          disabled={submitting}
        >
          {submitting
            ? t("AcceptAdminInvite.form.submitting")
            : t("AcceptAdminInvite.form.submit")}
        </button>
      </div>
    </form>
  );
};

export default AcceptAdminInvite;
